import { styled } from "goober";
import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../../../hooks/useMouseDownOutside";
import { playClick } from "../../../lib/sounds";
import { zIndexes } from "../../../lib/zIndexes";
import { Icon, type IconName } from "../../Icon";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";

const MENU_WIDTH = 180;

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: ${MENU_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MenuItem = styled("button")`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  &[data-destructive="true"] {
    color: var(--red-600);
  }
  &[data-destructive="true"]:hover {
    background: color-mix(in srgb, var(--red-600) 12%, transparent);
  }
  &[data-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &[data-disabled="true"]:hover {
    background: transparent;
  }
`;

const Divider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

export type MenuAction = {
  key: string;
  icon: IconName;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
};

type Props = {
  targetId: string;
  onView: () => void;
  onRemove?: () => void;
  removeLabel: string;
  removeReason?: string;
  actions: MenuAction[];
  onClose: () => void;
};

export function NodeContextMenu({
  targetId,
  onView,
  onRemove,
  removeLabel,
  removeReason,
  actions,
  onClose,
}: Props) {
  const [containerRef, menuRef] = useMouseDownOutside(onClose);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Keep the menu flush under its card's more button by writing styles directly
  // on scroll — no React state, so the graph never re-renders mid-scroll. Closes
  // once the card scrolls out of the graph viewport.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const scroll = document.querySelector("[data-ag-scroll]");
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const place = () => {
      const r = document
        .querySelector(`[data-node-id="${CSS.escape(targetId)}"] [data-more]`)
        ?.getBoundingClientRect();
      const sr = scroll?.getBoundingClientRect();
      if (
        !r ||
        !sr ||
        r.bottom < sr.top ||
        r.top > sr.bottom ||
        r.right < sr.left ||
        r.left > sr.right
      ) {
        onCloseRef.current();
        return;
      }
      el.style.left = `${Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8))}px`;
      el.style.top = `${Math.min(r.bottom + 4, window.innerHeight - h - 8)}px`;
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [targetId, menuRef]);

  const pick = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <Menu ref={menuRef} data-popover>
        <MenuItem onMouseDown={playClick} onClick={pick(onView)}>
          <Icon name="maximize-2" size={16} />
          <Text variant="text-regular">View</Text>
        </MenuItem>
        {onRemove ? (
          <MenuItem onMouseDown={playClick} onClick={pick(onRemove)}>
            <Icon name="circle-minus" size={16} />
            <Text variant="text-regular">{removeLabel}</Text>
          </MenuItem>
        ) : (
          <Tooltip content={removeReason ?? ""}>
            <MenuItem data-disabled="true">
              <Icon name="circle-minus" size={16} />
              <Text variant="text-regular">{removeLabel}</Text>
            </MenuItem>
          </Tooltip>
        )}
        {actions.length > 0 && <Divider />}
        {actions.map((action) =>
          action.disabled ? (
            <Tooltip key={action.key} content={action.disabledReason ?? ""}>
              <MenuItem
                data-destructive={action.destructive ? "true" : undefined}
                data-disabled="true"
              >
                <Icon name={action.icon} size={16} />
                <Text variant="text-regular">{action.label}</Text>
              </MenuItem>
            </Tooltip>
          ) : (
            <MenuItem
              key={action.key}
              data-destructive={action.destructive ? "true" : undefined}
              onMouseDown={playClick}
              onClick={pick(action.onClick)}
            >
              <Icon name={action.icon} size={16} />
              <Text variant="text-regular">{action.label}</Text>
            </MenuItem>
          )
        )}
      </Menu>
    </div>,
    document.body
  );
}
